import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, of, switchMap } from 'rxjs';
import { API_BASE_URL } from '@/app/core/http/api-base-url.token';
import { ApiEnvelope, unwrapEnvelope } from '@/app/core/http/api-envelope.model';
import { AuthHttpClient } from '@/app/core/http/auth-http-client';
import { rethrowWithParsedBody } from '@/app/core/http/blob-error-response.util';
import { toBffRut } from '@/app/shared/utils/rut.utils';
import {
  AvailablePeriods,
  BeneficiaryRegistryPage,
  BeneficiaryRegistryQuery,
  RegistryDownloadRequest,
  RegistryDownloadResult,
} from '../models/beneficiary-registry.model';
import {
  CatalogPage,
  GroupOption,
  PlanOption,
  RegistryCatalogQuery,
} from '../models/catalog.model';
import { BeneficiaryRegistryRepository } from './beneficiary-registry.repository';
import {
  isRegistryDownloadJobResponse,
  toRegistryDownloadFile,
  toRegistryDownloadJob,
} from './registry-download-response.parser';
import { toCatalogParams } from './catalog-params';

const PADRON_PATH = '/beneficiarios/padron';
const DOWNLOAD_PATH = `${PADRON_PATH}/descarga`;
const PLANS_PATH = `${PADRON_PATH}/planes`;
const GROUPS_PATH = `${PADRON_PATH}/grupos`;

@Injectable()
export class BeneficiaryRegistryHttpRepository extends BeneficiaryRegistryRepository {
  private readonly http = inject(AuthHttpClient);
  private readonly baseUrl = inject(API_BASE_URL);

  override getRegistry(query: BeneficiaryRegistryQuery): Observable<BeneficiaryRegistryPage> {
    return this.http
      .get<ApiEnvelope<BeneficiaryRegistryPage>>(`${this.baseUrl}${PADRON_PATH}`, {
        params: this.toParams(query),
      })
      .pipe(map((envelope) => unwrapEnvelope(envelope, PADRON_PATH)));
  }

  override getAvailablePeriods(): Observable<AvailablePeriods> {
    return this.http
      .get<ApiEnvelope<AvailablePeriods>>(`${this.baseUrl}${PADRON_PATH}/periodos`)
      .pipe(map((envelope) => unwrapEnvelope(envelope, `${PADRON_PATH}/periodos`)));
  }

  override getPlans(query: RegistryCatalogQuery): Observable<CatalogPage<PlanOption>> {
    return this.getCatalog<PlanOption>(PLANS_PATH, query);
  }

  override getGroups(query: RegistryCatalogQuery): Observable<CatalogPage<GroupOption>> {
    return this.getCatalog<GroupOption>(GROUPS_PATH, query);
  }

  override downloadRegistry(request: RegistryDownloadRequest): Observable<RegistryDownloadResult> {
    const { format, ...filters } = request;
    const filterParams = Object.fromEntries(
      Object.entries(this.toParams({ ...filters, page: 0, size: 0 })).filter(
        ([key]) => key !== 'page' && key !== 'size',
      ),
    );

    return this.http
      .getBlob(`${this.baseUrl}${DOWNLOAD_PATH}`, { params: { formato: format, ...filterParams } })
      .pipe(
        switchMap((response) =>
          isRegistryDownloadJobResponse(response)
            ? from(toRegistryDownloadJob(response, DOWNLOAD_PATH))
            : of(toRegistryDownloadFile(response, format)),
        ),
        catchError((cause: unknown) => from(rethrowWithParsedBody(cause))),
      );
  }

  private getCatalog<T>(path: string, query: RegistryCatalogQuery): Observable<CatalogPage<T>> {
    return this.http
      .get<ApiEnvelope<CatalogPage<T>>>(`${this.baseUrl}${path}`, {
        params: toCatalogParams(query),
      })
      .pipe(map((envelope) => unwrapEnvelope(envelope, path)));
  }

  private toParams(query: BeneficiaryRegistryQuery): Record<string, string | number> {
    const params: Record<string, string | number> = {
      page: query.page,
      size: query.size,
    };

    if (query.period) params['periodo'] = query.period;
    if (query.beneficiaryRut) params['rutBeneficiario'] = toBffRut(query.beneficiaryRut);
    if (query.planName) params['plan'] = query.planName;
    if (query.groupId !== undefined && query.groupName) {
      params['idGrupo'] = query.groupId;
      params['glosaGrupo'] = query.groupName;
    }

    return params;
  }
}
